import { useMutation } from '@apollo/client';
import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from "next/dynamic";
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo, useState } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import GuestPrescription from 'components/Others/GuestPrescription';
import cx from 'classnames';
import { wcApiDataService } from 'functions/wcApi';
import SelectPrescription from 'components/Others/SelectPrescription';

import {
  AttributeSelectedType,
  CategoryLensTypes,
  ColorTaxonomyType,
  CompositeComponentType,
  CompositeLensVariationType,
  GoogleReviewModel,
  ProductDetailType,
  SeoMetaTags
} from 'constants/dataTypes';

import { CREATE_CART } from 'gqlStrings/mutations/addToCart';

import { ProductFeatureBoxProps } from "constants/dataTypes";
import { getCategoryBasedSectionList } from 'functions/notion';
import { useGlassesStore } from 'zus/glassesStore';

import { getHashSlugFromColorSize } from 'components/ProductDetail/ProductOptionsComponent';
import { ProductDetailTabsComponent } from 'components/ProductDetail/ProductDetailTabsComponent';

import { ProductTitle } from 'components/ProductDetail/ProductTitle';
import ProductDetailBreadcrumb from 'components/ProductDetail/ProductDetailBreadcrumb';
import ProductImageSlider from 'components/ProductImageSlider/ProductImageSlider';
import ProductHeaderComponent from 'components/ProductDetail/ProductHeaderComponent';
import { ProductQuerySelector } from 'components/ProductDetail/ProductQuerySelector';
import * as ga from '../../../../lib/ga';
import { useProductStore } from 'zus/productStore';
import AfterPayInfo from 'components/Others/AfterPayInfo';
import ZipPayInfo from 'components/Others/ZipPayInfo';
import { getSession } from 'functions/session';
import { getFrameArmsOptions, getGoogleReviews, getSeoMetaTags } from 'utils/common.utils';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import GooglePageReviews from 'components/GoogleReview/GooglePageReviews';

const PDModal = dynamic(() => import('components/PD/PDModal'), { ssr: false });

import ShapeSwitcher from 'components/ProductDetail/ShapeSwitcher';
import ColorSizeFrameComponent from 'components/ProductDetail/ColorSizeFrameComponent';
import ColorSizeArmsComponent from 'components/ProductDetail/ColorSizeArmsComponent';
import LazyLoadedCommunityShowcase from 'components/ProductDetail/CommunityShowcaseLazyLoaded';
import CartRecommendedProductsLazyLoaded from 'components/ShoppingCart/CartRecommendedProductsLazyLoad';
import { ProductOptionsWithHex } from 'constants/dataDresdenTypes';
import { filterLensTypes } from 'functions/filterLensTypes';
import VariationSelector from 'components/ProductDetail/VariationSelector';
import DresdenGOAddToCartComponent from 'components/ProductDetail/DresdenGOAddToCartComponent';
import checkFrameArmsVariation from 'functions/checkFrameArmsVariation';
import { PrideDiscountBanner } from 'components/ProductDetail/PrideDiscountBanner';
const ProductPage: FC<{
  lensVariations: CompositeLensVariationType[];
  categoryLensTypes: CategoryLensTypes[];
  frameArmsOptions: ProductOptionsWithHex;
  seoMetaTags: SeoMetaTags;
  reviews: GoogleReviewModel;
}> = ({
  lensVariations,
  categoryLensTypes,
  frameArmsOptions,
  seoMetaTags,
  reviews
}) => {
    const router = useRouter();
    const [selectedVariations, setSelectedVariations] = useState<AttributeSelectedType>({});

    const purposeOptionZ = useGlassesStore((state) => state.purpose_option);
    const lens_thickness_selected = useGlassesStore((state) => state.lens_thickness_selected);

    const isActiveLensSelectionPanel = useProductStore((state: any) => state.isActiveLensSelectionPanel);

    const calculateAfterPay = useMemo(() => {
      if (lens_thickness_selected) {
        return lens_thickness_selected.price;
      }
      return purposeOptionZ?.price;
    }, [lens_thickness_selected, purposeOptionZ]);

    const [mutateCreateCart] = useMutation(CREATE_CART, {
      context: { clientName: 'laravel' }
    });

    useEffect(() => {
      if (process.browser && !getSession()) {
        mutateCreateCart({
          variables: {
            input: {
              isEmpty: true
            }
          }
        });
      }
    }, [mutateCreateCart]);

    const prepareProductName = () => {
      let productName = 'reading-glasses ' + router.query.product;
      if (router.query.armColor) {
        productName += ' with ' + router.query.armColor + ' arms';
      }
      return productName;
    }

    useEffect(() => {
      if (purposeOptionZ) {
        ga.addDataLayer({
          event: 'ImpCompleted',
          ecommerce: {
            detail: {
              actionField: { list: 'Shop' },
              products: [
                {
                  name: prepareProductName(),
                  price: purposeOptionZ.price,
                  category: 'reading-glasses',
                  variant: router.query.product
                }
              ]
            }
          }
        });
      }
    }, [router, purposeOptionZ]);

    const checkVariationOnColorPicker = (type: string, size: string, color: string) => {
      const shape = router.query.shape as string || 'classic';
      if (!frameArmsOptions) return false;
      return checkFrameArmsVariation(shape, frameArmsOptions, type, size, color);
    };

    if (router.isFallback) {
      return (
        <div className="flex h-[70vh] items-center justify-center">
          Loading...
        </div>
      );
    }

    return (
      <div className="relative w-full">
        <ProductHeaderComponent categorySlug="reading-glasses" reviews={reviews} />

        <div className={cx("mx-auto px-4 pt-6 relative", {
          "max-w-[1224px]": !isActiveLensSelectionPanel
        })}>
          <ProductDetailBreadcrumb categorySlug="reading-glasses" />

          <div className="grid lg:grid-cols-[650px,auto] gap-x-8 grid-cols-1 mt-[24px] gap-4">
            <div className="lg:flex lg:w-full lg:flex-col">
              <ProductTitle categorySlug="reading-glasses" categoryLensTypes={categoryLensTypes} showOnMobile={true} />
              <div className='sm:hidden xsm:block'>
                <PrideDiscountBanner />
              </div>
              <ProductImageSlider category="reading-glasses" frameColor={router.query.product as string} categoryLensTypes={categoryLensTypes} />
            </div>

            <div className="">
              <ProductTitle categorySlug="reading-glasses" categoryLensTypes={categoryLensTypes} />
              <div className='hidden sm:block'>
                <PrideDiscountBanner />
              </div>
              {!isActiveLensSelectionPanel && (
                <>
                  <ShapeSwitcher frameArmsOptions={frameArmsOptions} />
                  <ColorSizeFrameComponent
                    categorySlug="reading-glasses"
                    frameArmsOptions={frameArmsOptions}
                    frameColor={router.query.product as string}
                    checkVariationOnColorPicker={checkVariationOnColorPicker}
                  />
                  <ColorSizeArmsComponent
                    frameArmsOptions={frameArmsOptions}
                    frameColor={router.query.product as string}
                    checkVariationOnColorPicker={checkVariationOnColorPicker}
                  />
                </>
              )}
              <div className={cx({ ' lgx:h-[300px] lg:h-[550px]': isActiveLensSelectionPanel })}>
                <VariationSelector
                  attributesToShow={['attribute_pa_power']}
                  lensProducts={lensVariations}
                  selectedVariations={selectedVariations}
                  setSelectedVariations={setSelectedVariations}
                />
              </div>
              <DresdenGOAddToCartComponent
                category="reading-glasses"
                frameColor={router.query.product as string}
                checkVariationOnColorPicker={checkVariationOnColorPicker}
                selectedVariations={selectedVariations}
              />
            </div>
          </div>

          {!isActiveLensSelectionPanel && <ProductDetailTabsComponent categorySlug={'reading-glasses'} />}

          {!isActiveLensSelectionPanel && <CartRecommendedProductsLazyLoaded frameColorZ={router.query.product as string} shoppingCart={false} />}
          <LazyLoadedCommunityShowcase />
          <GooglePageReviews reviews={reviews} />
        </div>

        <LazyHydrate whenIdle>
          <PDModal />
        </LazyHydrate>
        <SelectPrescription />
        <GuestPrescription />
        <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
      </div>
    );
  }

export default ProductPage;

export const getStaticPaths: GetStaticPaths = async () => {
  var paths: any = [];
  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  var glassesProducts: any = await wcApiDataService('paths/shop', 'hasSubCategory&exclude_cat=spare-parts,frames,accessories,contact-lenses', '');
  if (!glassesProducts) {
    return {
      paths,
      fallback: 'blocking'
    };
  }

  for (const [country, data] of Object.entries(glassesProducts) as any) {
    for (const [category_slug, color_list] of Object.entries(data.items) as any) {
      color_list.map((color_id: any) => {
        paths.push({
          params: { category: category_slug, product: data.colors[color_id] },
          locale: country
        });
      })
    }
  }


  return {
    paths,
    fallback: 'blocking'
  };

};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const paramCat = 'reading-glasses';

  var productDetailPageData: any = await wcApiDataService('glasses/' + paramCat, '', locale);

  const glassesProductData = productDetailPageData?.components;

  if (!glassesProductData) {
    return {
      redirect: {
        destination: '/shop/reading-glasses',
        statusCode: 301
      }
    }
  }

  const lensVariations = glassesProductData.Lens.products[0].variations;

  const categoryLensTypes: CategoryLensTypes[] = filterLensTypes(glassesProductData);

  const frameArmsOptions = await getFrameArmsOptions(locale, 'dresdengo_frame_arms_options');
  const frameArmsOption = frameArmsOptions?.frame_options.find?.((item: any) => item.color_size.find?.((color_size: any) => color_size.color == params?.product));
  if (!frameArmsOption) {
    return {
      notFound: true
    };
  }

  const seoMetaTags = await getSeoMetaTags('/shop/reading-glasses/' + params?.product, locale);
  const reviews = await getGoogleReviews(locale);

  return {
    props: {
      lensVariations,
      categoryLensTypes,
      seoMetaTags,
      reviews,
      frameArmsOptions
    }
  };
};